import {
  Button,
  CheckCircleIcon,
  DeleteConfirmButton,
  DevDetailsButton,
  Flex,
  PencilSquareIcon,
  PlusIcon,
  Table,
  TableColumn,
  TableProps,
  Text,
  Tooltip,
  UserAvatar,
  XCircleIcon,
} from 'components'
import { useWorkspace } from 'components/providers'
import { DataType, SortDirection } from 'ka-table'
import { startCase } from 'lodash-es'
import { useEffect, useState } from 'react'
import { APIScope, IExternalClient } from 'types'
import { deleteExternalClient, getExternalClients, useBreakpoint, useUser } from 'utils'
import { defaultProps, makeTypeByField } from 'utils/tableUtils'
import { ApplicationInfo } from '../components'
import ResourceUpsell from '../components/subscription/ResourceUpsell'

export default function DeveloperPage() {
  const { workspace, subscription } = useWorkspace()
  const [tableProps, setTableProps] = useState(userTableProps)
  const [newApp, setNewApp] = useState(false)
  const [editingClient, setEditingClient] = useState<IExternalClient | null>()
  const [loading, setLoading] = useState(false)
  const isMobile = useBreakpoint() < 2

  const appCount = tableProps.data?.length || 0
  const showingInfo = Boolean(newApp || editingClient)

  useEffect(() => {
    handleClose()
  }, [workspace?.id])

  useEffect(() => {
    if (!workspace || showingInfo) return

    const fetchClients = async () => {
      setLoading(true)
      const externalClients = await (await getExternalClients({ workspaceId: workspace.id })).data
      if (!externalClients) return
      if (!externalClients.length) {
        setLoading(false)
        return setTableProps(s => ({ ...s, data: [] }))
      }

      const generatedCols = makeColumns(externalClients)
      const sortedColumn = tableProps.columns.find(c => c.sortDirection)
      const columns = generatedCols.map(c => {
        const defaultSort = c.key === 'created' ? SortDirection.Ascend : undefined
        const sortDirection = c.key === sortedColumn?.key ? sortedColumn.sortDirection : defaultSort
        return { ...c, sortDirection }
      })
      setTableProps(s => ({ ...s, columns, data: externalClients }))
      setLoading(false)
    }
    fetchClients()
  }, [workspace, isMobile, showingInfo])

  function handleClose() {
    setNewApp(false)
    setEditingClient(null)
  }

  function handleEdit(client: IExternalClient) {
    setEditingClient(client)
  }

  async function handleDelete(clientId: string) {
    try {
      setLoading(true)
      await deleteExternalClient({ id: clientId })
      setLoading(false)
    } catch (error) {
      console.error('Error deleting client:', error)
    }
  }

  if (!workspace) return

  const { plan } = subscription || {}
  if (plan !== 'enterprise' && plan !== 'pro') {
    return (
      <Flex flexDirection="column" gap={4} maxWidth="1100px" marginX={{ base: 4, md: 'auto' }}>
        <Text textStyle="h3" pt={4}>
          API Keys
        </Text>
        <ResourceUpsell resourceName="developer" variant="two" />
      </Flex>
    )
  }

  return (
    <Flex
      direction="column"
      gap={4}
      flex="auto"
      p={isMobile ? 4 : 0}
      pb={3}
      pr={5}
      overflowY="auto"
      overflowX="hidden"
    >
      <Flex direction="column">
        <Text textStyle="h3" pt={2}>
          API Keys
        </Text>
        <Text textStyle="h6">Generate and manage API keys for access to Momentify</Text>
      </Flex>
      {newApp || editingClient ? (
        <ApplicationInfo
          workspaceId={workspace.id}
          onClose={handleClose}
          app={newApp ? null : editingClient}
        />
      ) : (
        <>
          <Flex align="flex-end" justify="space-between">
            <Text textStyle="bodySmall" opacity={loading ? 0 : 1}>
              {appCount} Key{appCount === 1 ? '' : 's'}
            </Text>
            <Button
              leftIcon={<PlusIcon />}
              palette="primary"
              disabled={loading}
              onClick={() => setNewApp(true)}
            >
              New Application
            </Button>
          </Flex>
          <Flex bg="white" borderRadius="14px" overflowY="auto">
            <Table
              {...tableProps}
              emptyText="No applications found"
              onChange={setTableProps}
              loading={{ enabled: loading }}
            />
          </Flex>
        </>
      )}
    </Flex>
  )

  function makeColumns(externalClients: Partial<IExternalClient>[]): TableProps['columns'] {
    if (!externalClients.length) return []

    const clientCols = makeTypeByField(externalClients)
    const defCols = (
      isMobile ? ['status', 'title'] : ['status', 'title', 'created', 'createdBy', 'scopes']
    ).map(key => [key, clientCols[key]] as const)

    const columns = defCols.map(([key, value]) => ({
      isEditable: false,
      key,
      dataType: value,
      title: getColumnTitle(key),
      width: getWidth(key, isMobile),
      renderCell: getRenderCell(key),
      isResizable: true,
    }))

    columns.push({
      isEditable: false,
      key: 'actions',
      dataType: DataType.String,
      title: '',
      width: isMobile ? 35 : '10%',
      renderCell: props => {
        const client = props.rowData as IExternalClient
        return (
          <Flex gap={1} align="center">
            <DevDetailsButton
              items={[
                {
                  type: 'db',
                  id: client.id,
                  collection: 'externalClients',
                  label: 'Client',
                },
              ]}
            />
            <Button
              size="sm"
              variant="ghost"
              disabled={client.status === 'revoked'}
              leftIcon={<PencilSquareIcon fontSize="sm" opacity={0.6} color="black" />}
              onClick={() => handleEdit(client)}
            />
            <DeleteConfirmButton
              buttonLabel="Revoke"
              disabled={client.status === 'revoked'}
              description='Are you sure you want to revoke this API key? Any applications using it will immediately lose access. Type "Revoke" if you are certain you want to revoke this application key'
              onDelete={() => handleDelete(client.id)}
              onlyIcon
            />
          </Flex>
        )
      },
      isResizable: false,
    })

    return columns
  }

  function getRenderCell(key: string): TableColumn['renderCell'] {
    switch (key) {
      case 'status':
        return props => {
          const isActive = props.value === 'active'
          return (
            <Tooltip content={startCase(props.value)}>
              {isActive ? (
                <CheckCircleIcon color="green.500" type="outline" />
              ) : (
                <XCircleIcon color="red.500" type="outline" />
              )}
            </Tooltip>
          )
        }

      case 'scopes':
        return props => {
          const scopes = props.value
          return (
            <Text truncate pr={4}>
              {getScopeText(scopes)}
            </Text>
          )
        }

      case 'createdBy':
        return props => {
          const userId = props.value
          const user = useUser(userId)

          return (
            <Flex gap={3} align="center" py={2}>
              <UserAvatar user={user} />
              <Text textStyle="bodySmall">{user?.name}</Text>
            </Flex>
          )
        }
    }
  }
}

function getColumnTitle(key: string) {
  switch (key) {
    case 'title':
      return 'Name'
    default:
      return startCase(key)
  }
}

function getWidth(key: string, isMobile: boolean) {
  switch (key) {
    case 'created':
      return 50
    case 'status':
      return isMobile ? 25 : 35
    case 'title':
      return isMobile ? 50 : 80
    case 'scopes':
      return '40%'
    default:
      return 80
  }
}

function getScopeText(scopes: APIScope[]) {
  return scopes.map(scope => startCase(scope.toString().replace('_', ' '))).join(', ')
}

const userTableProps: TableProps = {
  ...defaultProps,
  rowKeyField: 'id',
}
